/**
 * Zebra Browser Print Service
 *
 * Communicates with Zebra Browser Print application running locally on port 9100.
 * Zebra Browser Print must be installed and running on the user's machine.
 * Download: https://www.zebra.com/us/en/support-downloads/printer-software/by-request-software.html
 */

const BROWSER_PRINT_URL = 'http://localhost:9100';

export interface ZebraPrinter {
  name: string;
  uid: string;
  connection: string;
  deviceType: string;
  version: number;
  provider: string;
  manufacturer: string;
}

export interface PrintResult {
  success: boolean;
  error?: string;
}

// Request deduplication: Zebra Browser Print can only handle one request at a time.
// Multiple React components calling checkBrowserPrintStatus simultaneously (especially
// with Strict Mode double-mounting) will flood it and cause timeouts.
let _statusPromise: Promise<boolean> | null = null;
let _printersPromise: Promise<ZebraPrinter[]> | null = null;

/**
 * Check if Zebra Browser Print is running.
 * Deduplicates concurrent calls — if a request is already in-flight, returns the same promise.
 */
export function checkBrowserPrintStatus(): Promise<boolean> {
  if (_statusPromise) return _statusPromise;

  _statusPromise = (async () => {
    try {
      const response = await fetch(`${BROWSER_PRINT_URL}/available`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      return response.ok;
    } catch (error) {
      console.warn('Zebra Browser Print not available:', error);
      return false;
    } finally {
      _statusPromise = null;
    }
  })();

  return _statusPromise;
}

/**
 * Get all available Zebra printers.
 * Deduplicates concurrent calls.
 */
export function getAvailablePrinters(): Promise<ZebraPrinter[]> {
  if (_printersPromise) return _printersPromise;

  _printersPromise = (async () => {
    try {
      const response = await fetch(`${BROWSER_PRINT_URL}/available`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });

      if (!response.ok) {
        console.warn('Zebra Browser Print returned non-OK status:', response.status);
        return [];
      }

      const data = await response.json();
      return data.printer || [];
    } catch (error) {
      console.warn('Zebra Browser Print not available:', error);
      return [];
    } finally {
      _printersPromise = null;
    }
  })();

  return _printersPromise;
}

/**
 * Send ZPL content to a specific printer
 */
export async function printZpl(
  printer: ZebraPrinter,
  zplContent: string
): Promise<PrintResult> {
  try {
    console.log('[ZebraPrint] Sending to printer:', printer);
    console.log('[ZebraPrint] ZPL content:', zplContent);

    // Browser Print API expects raw JSON with text/plain content type
    const requestBody = JSON.stringify({
      device: printer,
      data: zplContent,
    });
    console.log('[ZebraPrint] Request body:', requestBody);

    const response = await fetch(`${BROWSER_PRINT_URL}/write`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
      },
      body: requestBody,
      signal: AbortSignal.timeout(10000), // 10 second timeout for printing
    });

    const responseText = await response.text();
    console.log('[ZebraPrint] Response status:', response.status);
    console.log('[ZebraPrint] Response text:', responseText);

    if (!response.ok) {
      throw new Error(`Print failed: ${responseText || response.statusText}`);
    }

    return { success: true };
  } catch (error: any) {
    console.error('Failed to print ZPL:', error);
    return {
      success: false,
      error: error.message || 'Unknown print error',
    };
  }
}

/**
 * Read data from printer (for status checks)
 */
export async function readFromPrinter(
  printer: ZebraPrinter
): Promise<string | null> {
  try {
    // Browser Print API expects raw JSON with text/plain content type
    const requestBody = JSON.stringify({
      device: printer,
    });

    const response = await fetch(`${BROWSER_PRINT_URL}/read`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
      },
      body: requestBody,
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      return null;
    }

    return await response.text();
  } catch (error) {
    console.error('Failed to read from printer:', error);
    return null;
  }
}

/**
 * Get printer status using ZPL host status command
 */
export async function getPrinterStatus(
  printer: ZebraPrinter
): Promise<{ online: boolean; error?: string }> {
  try {
    // Send host status command
    const statusCommand = '~HS';
    await printZpl(printer, statusCommand);

    // Small delay to allow printer to respond
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Read response
    const response = await readFromPrinter(printer);

    if (response) {
      // Parse status response - basic check
      return { online: true };
    }

    return { online: true }; // Assume online if no error
  } catch (error: any) {
    return { online: false, error: error.message };
  }
}

/**
 * Print a test label to verify printer is working
 */
export async function printTestLabel(printer: ZebraPrinter): Promise<PrintResult> {
  const testZpl = `
^XA
^PW400
^LL200
^CF0,30
^FO50,30^FDZebra Test Print^FS
^FO50,70^CF0,20^FDPrinter: ${printer.name}^FS
^FO50,100^FDConnection: ${printer.connection}^FS
^FO50,130^FD${new Date().toLocaleString()}^FS
^FO50,160^GB300,2,2^FS
^XZ
`.trim();

  return printZpl(printer, testZpl);
}

// ============================================================================
// Font Management
// ============================================================================

/**
 * List font files stored on the printer's flash memory (E: drive).
 * Sends the ZPL directory list command and reads the response.
 * Returns raw printer response text, or null if read fails.
 */
export async function listPrinterFonts(
  printer: ZebraPrinter
): Promise<string | null> {
  await printZpl(printer, '^XA^WDE:*.TTF^XZ');
  await new Promise((resolve) => setTimeout(resolve, 1500));
  return readFromPrinter(printer);
}

/**
 * Download a TrueType font file to the printer's flash memory (E: drive).
 * The font persists across power cycles.
 *
 * @param printer - Target printer
 * @param fontData - TTF file contents as ArrayBuffer
 * @param fontName - Name to store on printer (e.g. 'NOTOSB' -> saved as E:NOTOSB.TTF).
 *                   Max 16 characters, no spaces.
 */
export async function downloadFontToPrinter(
  printer: ZebraPrinter,
  fontData: ArrayBuffer,
  fontName: string
): Promise<PrintResult> {
  // Sanitize fontName to prevent ZPL injection
  const sanitizedName = fontName.replace(/[^a-zA-Z0-9_-]/g, '');
  if (!sanitizedName || sanitizedName.length > 16) {
    return { success: false, error: 'Invalid font name (must be 1-16 alphanumeric/underscore/dash characters)' };
  }

  const bytes = new Uint8Array(fontData);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

  console.log(
    `[ZebraPrint] Downloading font ${sanitizedName}.TTF (${bytes.length} bytes) to printer ${printer.name}`
  );

  // ~DU = Download Unbounded file to E: (flash) drive
  const zpl = `~DUE:${sanitizedName}.TTF,${bytes.length},${hex}`;

  try {
    const requestBody = JSON.stringify({
      device: printer,
      data: zpl,
    });

    const response = await fetch(`${BROWSER_PRINT_URL}/write`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
      },
      body: requestBody,
      signal: AbortSignal.timeout(60000), // 60s timeout for large font files
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Font download failed: ${text || response.statusText}`);
    }

    console.log(`[ZebraPrint] Font ${sanitizedName}.TTF downloaded successfully`);
    return { success: true };
  } catch (error: any) {
    console.error(`[ZebraPrint] Failed to download font ${sanitizedName}:`, error);
    return {
      success: false,
      error: error.message || 'Font download failed',
    };
  }
}

/**
 * Delete a font file from the printer's flash memory.
 *
 * @param printer - Target printer
 * @param fileName - Full file name including extension (e.g. 'NOTOSB.TTF')
 */
export async function deletePrinterFont(
  printer: ZebraPrinter,
  fileName: string
): Promise<PrintResult> {
  // Sanitize fileName to prevent ZPL injection
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '');
  if (!sanitized || sanitized.length > 64) {
    return { success: false, error: 'Invalid font file name' };
  }
  return printZpl(printer, `^XA^IDE:${sanitized}^XZ`);
}
